/**
 * Ringtone using HTMLAudioElement + "muted autoplay priming" pattern
 * (industry standard — same as Twilio Voice, Zoom, 3CX WebClient).
 *
 *   1. On first user gesture: primeAudio() starts the <audio loop muted>
 *      so browser permits subsequent unmute without a fresh gesture.
 *   2. On incoming call:  startRingtone() → audio.muted = false.
 *   3. On accept/hangup:  stopRingtone() → audio.muted = true + reset.
 */

const RINGTONE_URL = '/ringtone.mp3';

let audio: HTMLAudioElement | null = null;
let primed = false;
let unlockInstalled = false;
let isPlaying = false;

function ensureAudio(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio(RINGTONE_URL);
    audio.loop = true;
    audio.muted = true;
    audio.preload = 'auto';
  }
  return audio!;
}

function installUnlock() {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const fire = () => primeAudio();
  window.addEventListener('pointerdown', fire, true);
  window.addEventListener('keydown', fire, true);
}
installUnlock();

/** Call from any user-gesture handler to prime the audio pipeline. Idempotent. */
export function primeAudio(): void {
  const a = ensureAudio();
  if (primed) {
    if (a.paused) a.play().catch(() => { /* */ });
    return;
  }
  a.muted = true;
  const p = a.play();
  if (p && typeof p.then === 'function') {
    p.then(() => { primed = true; console.log('[ringtone] primed (muted autoplay)'); })
     .catch((e) => console.warn('[ringtone] prime blocked:', e.message));
  } else {
    primed = true;
  }
}

export function startRingtone(): void {
  const a = ensureAudio();
  primeAudio();
  a.muted = false;
  a.currentTime = 0;
  isPlaying = true;
  const p = a.play();
  if (p && typeof p.then === 'function') {
    p.then(() => console.log('[ringtone] ring ON, playing:', !a.paused))
     .catch((e) => console.warn('[ringtone] ring play() failed:', e.message));
  }
}

export function stopRingtone(): void {
  if (!audio || !isPlaying) return;
  isPlaying = false;
  audio.muted = true;
  try { audio.pause(); } catch { /* */ }
  audio.currentTime = 0;
  console.log('[ringtone] stopped');
}
